import React from 'react';
import { ExtendedTheme, Heading } from 'neon-design-system';
import { createStyles, makeStyles } from '@material-ui/core';
import { usePreloadImage } from '../hooks/use-preload-image';

interface StyleProps {
	imageUrl?: string;
}

const useStyles = makeStyles<ExtendedTheme, StyleProps>((theme: ExtendedTheme) => createStyles({
	container: {
		flexDirection: 'column',
		justifyContent: 'flex-end',
		alignItems: 'flex-start',
		display: 'flex',
		'&::before': {
			position: 'absolute',
			top: 0,
			bottom: 0,
			left: 0,
			right: 0,
			content: '""',
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			// If this function is moved to wrap the outer selector, the :before style is applied muliple times as the browser resizes.
			// Seems to be related to https://github.com/mui-org/material-ui/issues/17126
			opacity: (props: StyleProps) => props.imageUrl ? 1 : 0,
			transition: 'opacity 0.3s ease-out',
			backgroundImage: (props: StyleProps) => props.imageUrl && `url('${props.imageUrl}')`
		},
		'&::after': {
			position: 'absolute',
			top: 0,
			bottom: 0,
			left: 0,
			right: 0,
			content: '""',
			backgroundImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.8) 100%)'
		},
		'&:hover::after': {
			backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.8))'
		},
		borderRadius: 8,
		overflow: 'hidden',
		height: 120,
		cursor: 'pointer',
		position: 'relative',
		backgroundColor: theme.palette.grey[400],
		[theme.breakpoints.up('md')]: {
			height: 200
		},
		[theme.breakpoints.up('xl')]: {
			height: 240
		}
	},
	title: {
		zIndex: 1,
		marginLeft: theme.spacing(2),
		marginBottom: theme.spacing(2),
		position: 'relative'
	}
}));

export interface CategoryCardProps {
	name: string;
	imageUrl: string;
	id: string;
}

const CategoryCard = (props: CategoryCardProps): JSX.Element => {
	const imageUrl = usePreloadImage(props.imageUrl);
	const classes = useStyles({ imageUrl });

	return (
		<div
			id={`sector-category-card-${props.name}`}
			className={classes.container}
		>
			<Heading
				styleName="title1"
				className={classes.title}
				color="white"
				xs="small"
				md="medium"
				xl={'large'}
			>
				{props.name}
			</Heading>
		</div>
	);
}

export default CategoryCard;
